import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../Config/config';
import ProctoringSystem from './ProctoringSystem';

const DiscountInterviewView = ({ userId, technology: propTech, experience: propExp }) => {
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    experience: propExp || '',
    position: '',
    primarySkills: propTech || ''
  });
  const [showForm, setShowForm] = useState(true);
  const [interviewId, setInterviewId] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0); // 0: Coding, 1-5: Tech Questions
  const [isCompleted, setIsCompleted] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(10 * 60); // Initial 10 mins for DSA
  const [phaseTimeLeft, setPhaseTimeLeft] = useState(10 * 60);
  const [warnings, setWarnings] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(71); // Python by default for easier coding
  const [executionOutput, setExecutionOutput] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [activeTab, setActiveTab] = useState('description'); // 'description', 'editorial', 'solutions'
  const [isExecuting, setIsExecuting] = useState(false);
  const [apiBaseUrl] = useState(`${API_BASE_URL}/one-time-interview`);
  const [facePresent, setFacePresent] = useState(true);
  const [faceWarningCount, setFaceWarningCount] = useState(0);
  const [questions, setQuestions] = useState(null);
  const [currentPhase, setCurrentPhase] = useState('coding'); // 'coding', 'tech'
  const [answers, setAnswers] = useState({ coding: '', tech: [] });
  const [report, setReport] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [showViolationPopup, setShowViolationPopup] = useState(false);
  const [violationMessage, setViolationMessage] = useState('');
  const lastViolationTimeRef = React.useRef(0);

  const handleStatusChange = useCallback((status) => {
    if (status === 'face_detected') {
      setFacePresent(true);
      setShowViolationPopup(false);
    }
  }, []);

  const faceViolationTypes = ['no_face_detected', 'multiple_faces_detected', 'head_turn_complete'];
  const headTurnViolationTypes = ['head_turn_complete'];

  const languages = [
    { id: 62, name: 'Java (OpenJDK 13.0.1)' },
    { id: 71, name: 'Python (3.8.1)' },
    { id: 63, name: 'JavaScript (Node.js 12.14.0)' },
    { id: 54, name: 'C++ (GCC 9.2.0)' },
    { id: 50, name: 'C (GCC 9.2.0)' }
  ];

  const executeCode = async () => {
    setIsExecuting(true);
    setExecutionOutput('Executing...');
    try {
        const sessionToken = localStorage.getItem('session_token');
        const response = await axios.post(`${apiBaseUrl}/execute`, {
            source_code: answers.coding,
            language_id: selectedLanguage,
            stdin: questions?.coding?.sample_input || ''
        }, {
            headers: { 
                'user-id': userId,
                'Authorization': `Bearer ${sessionToken}`,
                'X-Session-Token': sessionToken
            }
        });
        
        const { stdout, stderr, compile_output, status } = response.data;
        if (stdout) setExecutionOutput(stdout);
        else if (stderr) setExecutionOutput(`Error: ${stderr}`);
        else if (compile_output) setExecutionOutput(`Compile Error: ${compile_output}`);
        else if (status?.description) setExecutionOutput(`Status: ${status.description}`);
        else setExecutionOutput(`Execution complete (No output)`);
    } catch (err) {
        setExecutionOutput('Execution failed: ' + (err.response?.data?.error || err.message));
    } finally {
        setIsExecuting(false);
    }
  };

  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const sessionToken = localStorage.getItem('session_token');
        const response = await axios.get(`${apiBaseUrl}/eligibility`, {
            headers: { 
                'user-id': userId,
                'Authorization': `Bearer ${sessionToken}`,
                'X-Session-Token': sessionToken
            }
        });
        if (response.data.user) {
          setFormData(prev => ({
            ...prev,
            name: response.data.user.name || '',
            username: response.data.user.username || '',
            email: response.data.user.email || ''
          }));
        }
        if (!response.data.eligible) {
          setError(response.data.reason);
        }
      } catch (err) {
        console.error("Eligibility check failed", err);
      }
    };
    if (userId) checkEligibility();
  }, [userId, apiBaseUrl]);

  const startInterview = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.post(`${apiBaseUrl}/start`, {
        skill: formData.primarySkills, 
        experience: formData.experience, 
        position: formData.position,
        name: formData.name,
        username: formData.username,
        email: formData.email,
        user_id: userId
      }, {
        headers: { 
            'user-id': userId,
            'Authorization': `Bearer ${sessionToken}`,
            'X-Session-Token': sessionToken
        }
      });
      if (response.data.attempt_id) {
        setInterviewId(response.data.attempt_id);
        setQuestions(response.data.questions);
        setIsStarted(true);
        setShowForm(false);
        setPhaseTimeLeft(10 * 60); // Strictly 10 mins for Coding as per requirement
        // Request fullscreen on start
        try {
          if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen();
          }
        } catch (e) {
          console.warn("Fullscreen request failed", e);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to start interview');
    }
  };

  const submitInterview = useCallback(async (isTerminated = false) => {
    if (!interviewId) return;
    setIsCompleted(true); // Show final screen immediately
    setIsGeneratingReport(true);
    setReport(isTerminated ? 'Processing termination...' : 'Generating your AI Evaluation Report... Please wait. This may take up to 30 seconds.');
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.post(`${apiBaseUrl}/submit`, {
        attempt_id: interviewId,
        answers,
        proctoring_violations: warnings,
        user_id: userId,
        skill: formData.primarySkills,
        experience: formData.experience,
        position: formData.position,
        is_terminated: isTerminated
      }, {
        headers: { 
            'user-id': userId,
            'Authorization': `Bearer ${sessionToken}`,
            'X-Session-Token': sessionToken
        }
      });
      
      if (response.data.success) {
        // Handle new discount logic (fixed amounts or percentage)
        if (response.data.discount_amount) {
            setCoupon(response.data.discount_amount); // Store amount as "coupon" for display
            localStorage.setItem('pending_discount_amount', response.data.discount_amount);
        } else {
            setCoupon(response.data.discount);
            localStorage.setItem('pending_discount', response.data.discount);
        }
        
        setReport(response.data.report);
        setIsGeneratingReport(false);
        if (document.fullscreenElement) {
          document.exitFullscreen();
        }
      }
    } catch (err) {
      console.error('Failed to submit interview', err);
      setError("Failed to generate AI report. The results are still saved, please check your dashboard shortly.");
      setIsGeneratingReport(false);
      setReport("AI Evaluation failed to generate in time. Your performance was saved.");
    }
  }, [apiBaseUrl, interviewId, answers, warnings, userId, formData]);

  const nextQuestion = useCallback(() => {
    if (currentQuestionIndex < 5) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      if (currentQuestionIndex === 0) {
        setCurrentPhase('tech');
      }
      setPhaseTimeLeft(60); // 1 min for each subsequent question (total 5 mins for 5 questions)
    } else {
      submitInterview();
    }
  }, [currentQuestionIndex, submitInterview]);

  const handleViolation = useCallback(async (type, details) => {
    const now = Date.now();
    // Throttle head turn violations to 5 seconds to ensure sequential increments
    if (type === 'head_turn_complete' && now - lastViolationTimeRef.current < 5000) {
        return;
    }
    if (type === 'head_turn_complete') {
        lastViolationTimeRef.current = now;
    }

    if (type === 'no_face_detected') {
        setFacePresent(false);
    } else {
        setFacePresent(true);
    }

    console.warn('Violation detected:', type, details);
    
    // Log to backend
    if (interviewId) {
      try {
        const sessionToken = localStorage.getItem('session_token');
        await axios.post(`${apiBaseUrl}/integrity`, {
          interviewId,
          violationType: type,
          details: details
        }, { 
            headers: { 
                'user-id': userId,
                'Authorization': `Bearer ${sessionToken}`,
                'X-Session-Token': sessionToken
            } 
        });
      } catch (e) {
        console.error("Failed to log integrity violation", e);
      }
    }

    // Single state update for warnings and logic
    setWarnings(prev => {
        const newWarning = { type, timestamp: new Date() };
        const updatedWarnings = [...prev, newWarning];
        
        const tabSwitchCount = updatedWarnings.filter(w => ['tab_switch', 'window_switch'].includes(w.type)).length;
        const headTurnCount = updatedWarnings.filter(w => headTurnViolationTypes.includes(w.type)).length;

        if (tabSwitchCount >= 1) {
            setError("Interview terminated due to tab switching. No discount applied.");
            // Delay submit a bit to ensure user sees the error or it's logged
            setTimeout(() => submitInterview(true), 2000);
            return updatedWarnings; 
        }

        if (headTurnCount >= 3) {
            setError("Interview terminated due to repeated head turn violations. No discount applied.");
            setTimeout(() => submitInterview(true), 2000);
            return updatedWarnings;
        }
        
        // Generate warning message
        let message = '';
        if (type === 'tab_switch' || type === 'window_switch') {
            message = "Tab switching is strictly prohibited. Your interview is being terminated.";
        } else if (type === 'no_face_detected') {
             message = "Face Not Detected! Please stay visible to the camera.";
        } else if (type === 'head_turn_complete') {
             message = `Head turn detected. Warning ${headTurnCount}/2. (3rd time termination). Please keep your face towards the screen.`;
        } else if (faceViolationTypes.includes(type)) {
             message = `Proctoring violation: ${type.replace(/_/g, ' ')}. Please stay in frame.`;
        } else if (type === 'copy_paste') {
             message = "Copy-pasting is disabled during this interview.";
        }

        if (message) {
            setViolationMessage(message);
            setShowViolationPopup(true);
            if (window.violationTimeout) clearTimeout(window.violationTimeout);
            window.violationTimeout = setTimeout(() => setShowViolationPopup(false), 5000);
        }

        return updatedWarnings;
    });
  }, [apiBaseUrl, interviewId, submitInterview, faceViolationTypes, headTurnViolationTypes, userId]);

  useEffect(() => {
    if (isStarted && !isCompleted) {
      const handleVisibilityChange = () => {
        if (document.hidden) {
          handleViolation('tab_switch', { reason: 'visibility_hidden' });
        }
      };

      const handleBlur = () => {
        handleViolation('window_switch', { reason: 'window_blur' });
      };

      const handleCopyPaste = (e) => {
        e.preventDefault();
        handleViolation('copy_paste', { action: e.type });
        return false;
      };

      const handleContextMenu = (e) => {
        e.preventDefault();
        return false;
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('blur', handleBlur);
      document.addEventListener('copy', handleCopyPaste);
      document.addEventListener('paste', handleCopyPaste);
      document.addEventListener('cut', handleCopyPaste);
      document.addEventListener('contextmenu', handleContextMenu);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('blur', handleBlur);
        document.removeEventListener('copy', handleCopyPaste);
        document.removeEventListener('paste', handleCopyPaste);
        document.removeEventListener('cut', handleCopyPaste);
        document.removeEventListener('contextmenu', handleContextMenu);
      };
    }
  }, [isStarted, isCompleted, handleViolation]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center">
        <div className={`p-6 rounded-2xl shadow-lg border-l-4 bg-orange-50 border-orange-500 text-orange-900`} role="alert">
          <h3 className="font-bold text-xl mb-2">Attention</h3>
          <p className="text-lg">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-6 bg-orange-600 text-white px-8 py-3 rounded-full font-bold hover:bg-orange-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (showForm && !isStarted && !isCompleted) {
    return (
      <div className="p-8 max-w-2xl mx-auto bg-white shadow-2xl rounded-3xl border border-gray-100 mt-10">
        <h2 className="text-3xl font-black text-indigo-900 mb-6 text-center">Interview Details</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Full Name</label>
              <input 
                type="text"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Username</label>
              <input 
                type="text"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                placeholder="johndoe123"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Email Address</label>
            <input 
              type="email"
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="john@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Primary Skill</label>
            <input 
              type="text"
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
              value={formData.primarySkills}
              onChange={(e) => setFormData({...formData, primarySkills: e.target.value})}
              placeholder="e.g. Java, React, Python"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Experience</label>
            <select 
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
              value={formData.experience}
              onChange={(e) => setFormData({...formData, experience: e.target.value})}
            >
              <option value="">Select Experience</option>
              <option value="Fresher">Fresher</option>
              <option value="1-2 Years">1-2 Years</option>
              <option value="3-5 Years">3-5 Years</option>
              <option value="5+ Years">5+ Years</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Target Position</label>
            <input 
              type="text"
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
              value={formData.position}
              onChange={(e) => setFormData({...formData, position: e.target.value})}
              placeholder="e.g. Full Stack Developer"
            />
          </div>
          <button 
            onClick={() => setShowForm(false)}
            disabled={!formData.name || !formData.username || !formData.email || !formData.primarySkills || !formData.experience || !formData.position}
            className="w-full bg-indigo-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-indigo-700 transition disabled:opacity-50 mt-4"
          >
            Continue to Instructions
          </button>
        </div>
      </div>
    );
  }

  if (!isStarted) {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center bg-white shadow-lg rounded-2xl">
        <h2 className="text-3xl font-extrabold text-indigo-900 mb-4">One-Time Discount Interview</h2>
        <p className="text-gray-600 mb-6">
          Hello <b>{formData.name}</b>! Complete this structured interview for <b>{formData.position}</b> to earn massive discounts of up to <b>₹10,999</b>!
        </p>
        <div className="bg-indigo-50 p-6 rounded-xl mb-8 text-left border border-indigo-100">
            <h3 className="font-bold text-indigo-900 mb-3 text-sm uppercase tracking-wider">Customized for {formData.primarySkills} ({formData.experience}):</h3>
            <ul className="list-disc ml-5 space-y-2 text-indigo-800 text-sm">
              <li><b>Coding Task:</b> 1 {formData.experience} level Multi-language Coding Problem (15 mins)</li>
              <li><b>Technical:</b> 5 Domain-specific Questions for {formData.primarySkills} (2 mins each)</li>
              <li><b>Strict Proctoring:</b> No tab switching, no copy-pasting.</li>
              <li><b>Discounts:</b> Earn up to ₹7,999 off for 6-months plan and ₹10,999 off for 1-year plan! (3-month plan: no discount)</li>
            </ul>
        </div>
        <button 
          onClick={startInterview}
          className="bg-indigo-600 text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-indigo-700 transition transform hover:scale-105"
        >
          Start Now & Save
        </button>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="p-8 max-w-4xl mx-auto text-center bg-white shadow-2xl rounded-3xl border border-gray-100">
        <h2 className="text-4xl font-black mb-4 text-indigo-900">Congratulations!</h2>
        <p className="text-xl mb-8 text-gray-600">You've earned a special discount!</p>
        
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 text-indigo-900 p-8 rounded-2xl mb-8 border border-green-100">
          <p className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-2">Discount Earned</p>
          <h3 className="text-6xl font-bold text-green-600 mb-4">{coupon}% OFF</h3>
          <p className="text-lg text-gray-700">Applied automatically to your subscription.</p>
          <button 
            onClick={() => window.location.href = '/subscription-plans'}
            className="mt-6 bg-indigo-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-indigo-700 transition"
          >
            View Subscription Plans
          </button>
        </div>

        {report && (
          <div className="text-left bg-gray-50 p-6 rounded-xl mb-8 border border-gray-200 animate-in fade-in slide-in-from-bottom duration-1000 max-h-[400px] overflow-y-auto">
            <h4 className="font-bold text-gray-900 mb-4 border-b pb-2 flex items-center gap-2 sticky top-0 bg-gray-50 z-10">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                AI Performance Report
            </h4>
            <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed">{report}</div>
          </div>
        )}
        
        <button 
          onClick={() => {
            window.location.href = '/subscription-plans';
          }}
          className="bg-indigo-600 text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-indigo-700 transition"
        >
          See My Discounted Plans
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1e1e1e] font-sans selection:bg-orange-500/30">
      {/* Proctoring System */}
      {isStarted && !isCompleted && (
        <ProctoringSystem
          onViolation={handleViolation}
          onStatusChange={handleStatusChange}
          isActive={true}
        />
      )}

      {/* Violation Warnings Summary Overlay */}
      {showViolationPopup && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[100] w-full max-w-md animate-bounce">
          <div className="bg-red-600 text-white p-6 rounded-2xl shadow-2xl border-4 border-red-400 flex flex-col items-center text-center">
            <div className="text-4xl mb-2">⚠️</div>
            <h3 className="text-xl font-black uppercase mb-1">Proctoring Warning</h3>
            <p className="font-bold text-lg">{violationMessage}</p>
          </div>
        </div>
      )}

      {!facePresent && (
        <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4">
           <div className="bg-red-900 border-2 border-red-500 p-8 rounded-3xl max-w-md text-center shadow-[0_0_50px_rgba(239,68,68,0.5)]">
              <div className="text-6xl mb-4 animate-pulse">👤❌</div>
              <h2 className="text-2xl font-black text-white mb-4 uppercase tracking-tighter">Face Not Detected!</h2>
              <p className="text-red-200 font-bold mb-6">
                We cannot see you. Please ensure your face is clearly visible to the camera.
              </p>
              <div className="bg-black/40 py-3 rounded-xl border border-red-500/30">
                <p className="text-red-400 text-sm font-mono">STAY IN THE FRAME TO CONTINUE</p>
              </div>
           </div>
        </div>
      )}

      {/* Top Navbar */}
      <div className="h-14 bg-[#282828] border-b border-[#3e3e3e] flex items-center justify-between px-4 text-gray-300">
        <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 cursor-pointer hover:bg-[#3e3e3e] p-2 rounded-lg transition" onClick={() => window.location.href = '/dashboard'}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>
            </div>
            <h1 className="font-bold text-sm tracking-tight flex items-center gap-2">
                <span className="text-orange-500">SkillGenie</span>
                <span className="text-gray-500">|</span>
                <span>One-Time Interview</span>
            </h1>
            <div className="flex items-center gap-2 text-xs font-bold ml-4">
                {[0, 1, 2, 3, 4, 5].map(i => (
                    <div key={i} className={`px-3 py-1 rounded ${currentQuestionIndex === i ? 'bg-orange-600 text-white' : 'bg-[#3e3e3e] text-gray-400'}`}>
                        Q{i + 1}
                    </div>
                ))}
            </div>
        </div>
        <div className="flex items-center gap-4">
            <div className={`px-4 py-1.5 rounded-lg font-mono text-sm font-bold border ${phaseTimeLeft < 60 ? 'bg-red-500/20 border-red-500 text-red-500 animate-pulse' : 'bg-[#3e3e3e] border-[#4a4a4a] text-orange-500'}`}>
                {formatTime(phaseTimeLeft)}
            </div>
            <button 
                onClick={() => {
                    if (window.confirm("Are you sure you want to terminate the interview? You will NOT receive any scholarship discount.")) {
                        submitInterview(true);
                    }
                }}
                className="bg-red-500/10 hover:bg-red-500/20 text-red-500 text-[10px] font-black px-4 py-1.5 rounded-lg border border-red-500/20 transition uppercase"
            >
                Terminate
            </button>
            <button 
                onClick={() => currentQuestionIndex === 5 ? submitInterview(false) : nextQuestion()}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-1.5 rounded-lg font-bold text-sm transition shadow-lg"
            >
                {currentQuestionIndex === 5 ? 'Submit' : 'Next'}
            </button>
        </div>
      </div>

      <div className="flex h-[calc(100vh-3.5rem)] p-2 gap-2 overflow-hidden">
        {/* Left Pane: Question Description */}
        <div className="flex-1 bg-[#282828] rounded-xl flex flex-col overflow-hidden border border-[#3e3e3e]">
            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar text-gray-300">
                {currentQuestionIndex === 0 && questions?.coding && (
                    <div className="animate-in fade-in duration-500">
                        <div className="flex items-center gap-2 mb-4">
                            <h2 className="text-2xl font-bold text-white">{questions.coding.title}</h2>
                            <span className="bg-orange-500/20 text-orange-500 text-[10px] font-black px-2 py-0.5 rounded uppercase">{questions.coding.difficulty || 'Hard'}</span>
                        </div>
                        
                        <div className="prose prose-invert max-w-none space-y-6">
                            <p className="text-[#eff1f6fb] leading-relaxed whitespace-pre-wrap">{questions.coding.description}</p>
                            
                            <div className="space-y-4">
                                <h3 className="text-lg font-bold text-white border-b border-[#3e3e3e] pb-2">Example 1:</h3>
                                <div className="bg-[#3e3e3e]/30 p-4 rounded-xl border border-[#3e3e3e] font-mono text-sm">
                                    <p><span className="text-gray-400 font-bold">Input:</span> <span className="text-white">{questions.coding.sample_input}</span></p>
                                    <p><span className="text-gray-400 font-bold">Output:</span> <span className="text-white">{questions.coding.sample_output}</span></p>
                                    {questions.coding.explanation && (
                                        <p className="mt-2"><span className="text-gray-400 font-bold">Explanation:</span> <span className="text-gray-300 italic">{questions.coding.explanation}</span></p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 mt-8">
                                <div className="bg-[#3e3e3e]/20 p-4 rounded-xl border border-[#3e3e3e]">
                                    <h4 className="text-xs font-black text-gray-500 uppercase mb-2">Constraints</h4>
                                    <ul className="list-disc ml-5 text-sm text-gray-400 space-y-1">
                                        <li className="whitespace-pre-wrap">{questions.coding.constraints}</li>
                                        <li>Time limit: 1000ms</li>
                                        <li>Memory limit: 256MB</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {currentQuestionIndex >= 1 && questions?.others && (
                    <div className="flex flex-col items-center justify-center h-full text-center max-w-lg mx-auto animate-in zoom-in duration-500">
                        <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mb-6 border border-emerald-500/30">
                            <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/></svg>
                        </div>
                        <h3 className="text-xs font-black text-emerald-500 uppercase tracking-[0.2em] mb-2">Technical Proficiency</h3>
                        <p className="text-2xl font-bold text-white leading-tight mb-4">{questions.others[currentQuestionIndex - 1]}</p>
                        <p className="text-gray-500 text-sm">Explain clearly and concisely. You can use examples in your text.</p>
                    </div>
                )}
            </div>
        </div>

        {/* Right Pane: Code Editor & Console */}
        <div className="flex-[1.2] flex flex-col gap-2">
            {/* Top: Editor Area */}
            <div className="flex-[2.5] bg-[#282828] rounded-xl border border-[#3e3e3e] flex flex-col overflow-hidden shadow-2xl">
                <div className="flex items-center justify-between bg-[#333] px-4 py-2 border-b border-[#3e3e3e]">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span className="text-xs font-bold text-gray-300">Code Editor</span>
                    </div>
                    {currentQuestionIndex === 0 && (
                        <select 
                            className="bg-[#1e1e1e] text-white text-[10px] font-bold px-3 py-1.5 rounded-lg border border-[#3e3e3e] focus:outline-none focus:ring-1 focus:ring-orange-500"
                            value={selectedLanguage}
                            onChange={(e) => setSelectedLanguage(Number(e.target.value))}
                        >
                            {languages.map(lang => (
                                <option key={lang.id} value={lang.id}>{lang.name}</option>
                            ))}
                        </select>
                    )}
                </div>
                
                <div className="flex-1 relative">
                    <textarea 
                        className={`w-full h-full p-6 font-mono text-sm bg-[#1e1e1e] text-[#d4d4d4] resize-none focus:outline-none selection:bg-[#264f78] custom-scrollbar ${currentQuestionIndex === 0 ? 'block' : 'hidden'}`}
                        value={answers.coding}
                        onChange={(e) => setAnswers({...answers, coding: e.target.value})}
                        placeholder={`// Write your solution here...`}
                        spellCheck="false"
                        onCopy={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                    />
                    
                    {currentQuestionIndex >= 1 && (
                        <textarea 
                            className="w-full h-full p-8 font-sans text-lg bg-[#1e1e1e] text-[#d4d4d4] resize-none focus:outline-none custom-scrollbar"
                            value={answers.tech[currentQuestionIndex - 1] || ''}
                            onChange={(e) => {
                                const newTech = [...answers.tech];
                                newTech[currentQuestionIndex - 1] = e.target.value;
                                setAnswers({...answers, tech: newTech});
                            }}
                            placeholder={`Type your detailed answer here...`}
                            spellCheck="false"
                            onCopy={(e) => e.preventDefault()}
                            onPaste={(e) => e.preventDefault()}
                        />
                    )}
                </div>
            </div>

            {/* Bottom: Console / Results Area */}
            <div className="flex-1 bg-[#282828] rounded-xl border border-[#3e3e3e] flex flex-col overflow-hidden shadow-2xl">
                <div className="flex items-center justify-between bg-[#333] px-4 py-2 border-b border-[#3e3e3e]">
                    <div className="flex gap-4">
                        <button className="text-xs font-bold text-white border-b-2 border-green-500 pb-0.5">Testcase</button>
                        <button className="text-xs font-bold text-gray-500 hover:text-gray-300 transition">Test Result</button>
                    </div>
                    {currentQuestionIndex === 0 && (
                        <button 
                            onClick={executeCode}
                            disabled={isExecuting}
                            className="bg-[#3e3e3e] hover:bg-[#4a4a4a] text-white text-[10px] font-black px-4 py-1.5 rounded-lg border border-[#4a4a4a] transition flex items-center gap-2 disabled:opacity-50"
                        >
                            {isExecuting && <div className="w-2 h-2 rounded-full bg-white animate-ping"></div>}
                            {isExecuting ? 'EXECUTING...' : 'RUN CODE'}
                        </button>
                    )}
                </div>
                <div className="flex-1 bg-[#1e1e1e] p-4 font-mono text-sm overflow-y-auto custom-scrollbar">
                    {executionOutput ? (
                        <div className="animate-in slide-in-from-bottom duration-300">
                             <div className="text-[10px] font-black text-gray-500 uppercase mb-2 flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                                Execution Output
                             </div>
                             <pre className={`whitespace-pre-wrap ${executionOutput.includes('Error') || executionOutput.includes('failed') ? 'text-red-400' : 'text-green-400'}`}>
                                {executionOutput}
                             </pre>
                        </div>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-gray-600">
                            <svg className="w-8 h-8 mb-2 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                            <p className="text-xs font-bold uppercase tracking-widest">You must run your code first</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
      </div>
      
      {/* Violation Warnings Summary Overlay */}
      {warnings.length > 0 && (
          <div className="fixed bottom-4 left-4 flex gap-2 z-[50]">
              {warnings.filter(w => ['tab_switch', 'window_switch'].includes(w.type)).length > 0 && (
                  <div className="bg-red-600 text-white px-3 py-1 rounded-md text-[10px] font-black uppercase shadow-lg">
                    Integrity: Compromised
                  </div>
              )}
              {warnings.filter(w => faceViolationTypes.includes(w.type)).length > 0 && (
                  <div className="bg-orange-600 text-white px-3 py-1 rounded-md text-[10px] font-black uppercase shadow-lg animate-pulse">
                    Proctoring: {warnings.filter(w => faceViolationTypes.includes(w.type)).length} / 5
                  </div>
              )}
          </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #1e1e1e; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #3e3e3e; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #4a4a4a; }
      `}} />
    </div>
  );
};

export default DiscountInterviewView;
